import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { currentOrgId } from "../api/client";
import type { CustomerDetail, Onboarding, VendorDetail } from "../api/types";
import type { SalesDoc } from "../api/sales-types";
import { useAuth } from "../auth/AuthContext";
import { t, tx, useLocale } from "../i18n";
import BrandMark from "../ui/BrandMark";
import { fmtMoney } from "../ui/currency/money";
import { cap, fmtDate, fmtDateTime } from "../ui/format";
import { Drawer, Empty, Facts, Pill } from "../ui/parts";
import type { View } from "../ui/routes";
import { keptRecords, TTL_HOURS, type Kept, type OfflineProfile } from "./offlineCache";
import { useNetwork } from "./network";
import { discard, exportItem, inLeftOrg, retry, useOutbox, type OutboxItem } from "./outbox";
import { OfflineFormDrawer, type OfflineForm } from "./OfflineForms";
import { ActionConflict, EditConflict, FixableIssue, IssueFlagged, RefusedIssue } from "./ConflictView";
import { FixIssue, FullFormDrawer, type FullForm } from "./OfflineFullForms";
import { resendFixed } from "./outbox";
import { serverClock } from "./numberBlocks";
import { keptLatest } from "./offlineCache";
import type { TimeEntry } from "../api/projects";
import type { Expense } from "../api/types";
import { ActionDrawer, ApprovalsSection, BillingSection, BlockNote, mayDo, OnboardingView, totalOf, type ActionForm } from "./OfflineActions";
import { devicePref, setDevicePref } from "./orgCheck";
import { applyUpdate, install, usePwa } from "./register";

/* The phone app's screens and strips: the offline banner (in Shell), the outbox, the kept records, the offline screen after
 * a reload with no connection (App.tsx), and the install card (Settings › App). Wave 8: the conflict view that opens over a
 * form when an online edit met someone else's change, and the offline screen's edits, issuing, approvals and payments. */

/** Under the header, only when there is something to say: offline, changes waiting or in conflict, a new build ready. */
export function PwaBanner({ notify, go }: { notify: (m: string) => void; go: (v: View, id?: string | null) => void }) {
  const { online } = useNetwork();
  const { updateReady } = usePwa();
  const items = useOutbox();
  const [open, setOpen] = useState<null | "outbox" | "kept">(null);
  // Wave 8: an online edit that met someone else's change (outbox.ts editOrQueue): the conflict view, over the form.
  const [clash, setClash] = useState<string | null>(null);
  useEffect(() => {
    const on = (e: Event) => setClash((e as CustomEvent<{ record: string }>).detail.record);
    window.addEventListener("crm:edit-conflict", on);
    return () => window.removeEventListener("crm:edit-conflict", on);
  }, []);
  // Wave 7: an invoice issued offline whose totals the server worked out differently is sent, but waits to be read.
  const conflicts = items.filter((i) => i.state === "conflict" || i.state === "flagged").length;
  const waiting = items.length - conflicts;

  useEffect(() => {
    const sent = (e: Event) => {
      const { sent: n, conflicts: c } = (e as CustomEvent<{ sent: number; conflicts: number }>).detail;
      if (n) notify(n === 1 ? t("1 change made offline was sent.") : t("{n} changes made offline were sent.", { n }));
      if (c) notify(c === 1 ? t("1 offline change could not be sent. Open the outbox to see why.") : t("{n} offline changes could not be sent. Open the outbox to see why.", { n: c }));
    };
    window.addEventListener("crm:outbox-sent", sent);
    return () => window.removeEventListener("crm:outbox-sent", sent);
  }, [notify]);

  // Wave 6: everyone can install from here, not only roles that can open Settings › App. "Not now" holds for 30 days.
  const { canInstall, installed } = usePwa();
  const [installLater, setInstallLater] = useState(true);
  useEffect(() => void devicePref<boolean>(INSTALL_PREF).then((p) => setInstallLater(Boolean(p && Date.now() - p.at < 30 * 86_400_000)), () => setInstallLater(false)), []);
  const later = () => {
    setInstallLater(true);
    void setDevicePref(INSTALL_PREF, true);
  };
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);

  const strips: ReactNode[] = [];
  if (!installed && !installLater && (canInstall || ios))
    strips.push(
      <div key="inst" className="pwa-strip is-install" role="status">
        <span>{canInstall ? t("Install the CRM as an app on this device: it opens from the home screen and works for a while offline.") : t("Add the CRM to your Home Screen: in Safari tap Share, then Add to Home Screen.")}</span>
        <span className="pwa-actions">
          {canInstall && (
            <button type="button" className="button button--primary button--sm" onClick={() => void install().then((ok) => (ok ? notify(t("Installed. Open it from your home screen or app list.")) : later()))}>
              {t("Install app")}
            </button>
          )}
          <button type="button" className="button button--secondary button--sm" onClick={later}>
            {t("Not now")}
          </button>
        </span>
      </div>,
    );
  if (!online)
    strips.push(
      <div key="off" className="pwa-strip is-offline" role="status">
        <span>
          <strong>{t("You're offline.")}</strong> {t("Records you opened recently can be read, changed and approved, and invoices issued with this device's reserved numbers; all of it is kept on this device and sent when you're back.")}
        </span>
        <button type="button" className="button button--secondary button--sm" onClick={() => setOpen("kept")}>
          {t("Saved for offline")}
        </button>
      </div>,
    );
  if (items.length)
    strips.push(
      <div key="out" className={`pwa-strip${conflicts ? " is-conflict" : ""}`} role="status">
        <span>
          {conflicts ? <strong>{conflicts === 1 ? t("1 needs your attention.") : t("{n} need your attention.", { n: conflicts })}</strong> : null} {waiting ? t("{n} waiting to be sent.", { n: waiting }) : null}
        </span>
        <button type="button" className="button button--secondary button--sm" onClick={() => setOpen("outbox")}>
          {t("Outbox")} ({items.length})
        </button>
      </div>,
    );
  if (updateReady)
    strips.push(
      <div key="upd" className="pwa-strip" role="status">
        <span>{t("A new version of the CRM is ready.")}</span>
        <button type="button" className="button button--primary button--sm" onClick={applyUpdate}>
          {t("Reload")}
        </button>
      </div>,
    );

  return (
    <>
      {strips.length > 0 && <div className="pwa-strips">{strips}</div>}
      {open === "outbox" && <OutboxDrawer items={items} onClose={() => setOpen(null)} onOpenInvoice={(id) => (setOpen(null), go("salesinvoices", id))} />}
      {open === "kept" && <KeptDrawer onClose={() => setOpen(null)} onOpen={(r) => (setOpen(null), go(r.kind === "customer" ? "customers" : r.kind === "vendor" ? "vendors" : r.kind === "onboarding" ? "onboarding" : "salesinvoices", idOf(r)))} />}
      {clash && <ClashDrawer record={clash} items={items} notify={notify} onClose={() => setClash(null)} />}
    </>
  );
}

/**
 * Wave 8: two people edited the same record online. The other change is on the server; nothing of this person's was written.
 * Both versions, field by field, as for an offline edit; it closes once they have chosen (or stays in the outbox if closed).
 */
function ClashDrawer({ record, items, notify, onClose }: { record: string; items: OutboxItem[]; notify: (m: string) => void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const mine = items.filter((i) => i.kind === "edit" && i.record === record && i.state === "conflict" && i.conflict);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (mine.length) setSeen(true);
    else if (seen) {
      notify(t("Saved with your choices. Close the form and open the record again to see it."));
      onClose();
    }
  }, [mine.length, seen, notify, onClose]);
  if (!mine.length) return null;
  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    void fn().finally(() => setBusy(false));
  };
  return createPortal(
    <Drawer open title={t("Someone else changed this record")} sub={t("They saved while you had it open. Nothing of yours was written yet: choose what to keep. Closing this leaves it in the outbox.")} onClose={onClose}>
      {mine.map((i) => (
        <div key={i.id}>
          <strong>{tx(i.label)}</strong>
          <EditConflict item={i} busy={busy} run={run} />
        </div>
      ))}
    </Drawer>,
    document.body,
  );
}

const idOf = (r: Kept) => r.key.split("/").pop() ?? "";
/** Wave 9: the record a full form edits, by its kind. */
type FullRecord<K extends FullForm["kind"]> = NonNullable<Extract<FullForm, { kind: K }> extends { record?: infer R } ? R : never>;

/** Wave 8: discarding an unsent offline issue spends its number (it was printed with it); the person is told first. */
function discardQuestion(i: OutboxItem) {
  const number = i.kind === "issue" ? (i.body as { number?: unknown }).number : null;
  if (typeof number === "string") return t("Discard “{what}”? It will not be sent. {number} was printed on this invoice, so it is marked as used on this device and refused, and never goes on another invoice.", { what: i.label, number });
  return t("Discard “{what}”? It will not be sent.", { what: i.label });
}
const INSTALL_PREF = "install-later";

/** What waits to be sent, and what the server refused, with Retry and Discard. */
export function OutboxDrawer({ items, onClose, onOpenInvoice }: { items: OutboxItem[]; onClose: () => void; onOpenInvoice?: (id: string) => void }) {
  const { online } = useNetwork();
  const [busy, setBusy] = useState<string | null>(null);
  const run = (id: string, fn: () => Promise<void>) => {
    setBusy(id);
    void fn().finally(() => setBusy(null));
  };
  // Wave 9: an issue refused for a reason this device can fix, open in the full invoice form; the time by the server's clock.
  const [fixing, setFixing] = useState<OutboxItem | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => void serverClock().then(setNow, () => undefined), [items]);
  return (
    <>
    <Drawer open title="Outbox" sub="Made on this device while offline. Each is sent once, even if it is tried again." onClose={onClose}>
      {!items.length && <Empty>Nothing is waiting.</Empty>}
      <ul className="pwa-list">
        {items.map((i) => {
          // Made in another organization than this screen's: it is sent there, and its invoice opens only there.
          const elsewhere = (i.org ?? 1) !== (currentOrgId() ?? 1);
          const issued = i.kind === "issue" ? (i.body as { invoice_id?: string }).invoice_id : null;
          const invoiceId = !elsewhere ? (i.kind === "payment" ? (i.path.startsWith("/admin/sales/") ? i.path.split("/")[4] : null) : (issued ?? null)) : null;
          // Made in an organization the person has left: never sent; keep it as a file, or let it go.
          const left = inLeftOrg(i);
          return (
            <li key={i.id} className={i.state !== "pending" ? "is-conflict" : undefined}>
              <div>
                <strong>{tx(i.label)}</strong>
                <small>
                  {t("Made {when}", { when: fmtDateTime(i.created_at) })}
                  {i.receipt ? ` · ${t("with a receipt photo")}` : ""}
                  {i.stage === "receipt" ? ` · ${t("expense recorded, photo still to send")}` : ""}
                  {elsewhere ? ` · ${t("for {org}", { org: i.org_name ?? t("organization {n}", { n: i.org ?? 1 }) })}` : ""}
                </small>
                {left ? (
                  <p className="pwa-error" role="alert">
                    {t("You no longer belong to {org}, so this will not be sent. Export it to keep what you typed, or discard it.", { org: i.org_name ?? t("organization {n}", { n: i.org ?? 1 }) })}
                  </p>
                ) : i.state === "flagged" ? (
                  <IssueFlagged item={i} />
                ) : i.state === "conflict" && i.fixable ? (
                  <FixableIssue item={i} now={now} busy={busy === i.id} onFix={() => setFixing(i)} onSend={() => run(i.id, () => resendFixed(i.id))} />
                ) : i.state === "conflict" && i.refused ? (
                  <RefusedIssue item={i} />
                ) : i.state === "conflict" && i.conflict && i.kind === "edit" ? (
                  <EditConflict item={i} busy={busy === i.id} run={(fn) => run(i.id, fn)} />
                ) : i.state === "conflict" && i.conflict ? (
                  <ActionConflict item={i} busy={busy === i.id} online={online} run={(fn) => run(i.id, fn)} />
                ) : i.state === "conflict" ? (
                  <p className="pwa-error" role="alert">
                    {i.error}
                  </p>
                ) : (
                  <Pill tone="amber">{online ? "Sending…" : "Waiting for a connection"}</Pill>
                )}
              </div>
              {(i.state === "flagged" || (i.state === "conflict" && i.refused)) && (
                <span className="pwa-actions">
                  {invoiceId && onOpenInvoice && (
                    <button type="button" className="button button--secondary button--sm" onClick={() => onOpenInvoice(invoiceId)}>
                      {t("Open invoice")}
                    </button>
                  )}
                  <button type="button" className="button button--secondary button--sm" disabled={busy === i.id} onClick={() => run(i.id, () => discard(i.id))}>
                    {t("Got it")}
                  </button>
                </span>
              )}
              {((i.state === "conflict" && !i.refused) || left) && (
                <span className="pwa-actions">
                  {/* A conflict with the server's copy would only meet the same change again: it is settled above instead. */}
                  {!left && !i.conflict && !i.fixable && (
                    <button type="button" className="button button--secondary button--sm" disabled={busy === i.id || !online} onClick={() => run(i.id, () => retry(i.id))}>
                      {t("Try again")}
                    </button>
                  )}
                  <button type="button" className="button button--secondary button--sm" disabled={busy === i.id} onClick={() => run(i.id, () => exportItem(i))}>
                    {t("Export")}
                  </button>
                  {invoiceId && onOpenInvoice && (
                    <button type="button" className="button button--secondary button--sm" onClick={() => onOpenInvoice(invoiceId)}>
                      {t("Open invoice")}
                    </button>
                  )}
                  <button type="button" className="button button--secondary button--sm" disabled={busy === i.id} onClick={() => window.confirm(discardQuestion(i)) && run(i.id, () => discard(i.id))}>
                    {t("Discard")}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Drawer>
    {/* After the outbox, so the invoice form opens over it. */}
    {fixing && <FixIssue item={fixing} onClose={() => setFixing(null)} onDone={() => setFixing(null)} />}
    </>
  );
}

/** `bump` (wave 8): read again, e.g. after an offline edit changed a kept copy. */
function useKept(bump = 0) {
  const [kept, setKept] = useState<Kept[] | null>(null);
  useEffect(() => void keptRecords().then(setKept, () => setKept([])), [bump]);
  return kept;
}

/** What a kept record is (wave 9: estimates, items and expenses as opened are listed too). */
function kindLabel(kind: Kept["kind"]) {
  if (kind === "customer") return t("Customer");
  if (kind === "vendor") return t("Vendor");
  if (kind === "onboarding") return t("Onboarding");
  if (kind === "estimate") return t("Estimate");
  if (kind === "item") return t("Item");
  if (kind === "expense") return t("Expense");
  return t("Invoice");
}

function KeptList({ kept, onOpen }: { kept: Kept[] | null; onOpen: (r: Kept) => void }) {
  if (!kept) return null;
  if (!kept.length) return <Empty>{t("Nothing is saved yet. Customers, vendors and invoices you open while online are kept here for {TTL_HOURS} hours.", { TTL_HOURS })}</Empty>;
  return (
    <ul className="pwa-list">
      {kept.map((r) => (
        <li key={r.key}>
          <button type="button" className="pwa-row" onClick={() => onOpen(r)}>
            <strong>{tx(r.label)}</strong>
            <small>
              {kindLabel(r.kind)}
              {r.sub ? ` · ${r.sub}` : ""} · {t("saved {when}", { when: fmtDateTime(new Date(r.saved_at).toISOString()) })}
            </small>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function KeptDrawer({ onClose, onOpen }: { onClose: () => void; onOpen: (r: Kept) => void }) {
  const kept = useKept();
  return (
    <Drawer open title="Saved for offline" sub={t("The customers, vendors and invoices you opened last, as they were then. Deleted on sign-out and after {TTL_HOURS} hours.", { TTL_HOURS })} onClose={onClose}>
      <KeptList kept={kept} onOpen={onOpen} />
    </Drawer>
  );
}

/** A kept record, read-only. */
function KeptView({ r }: { r: Kept }) {
  // Wave 9: an item and an expense as opened, in short; each opens in its full form to change it.
  if (r.kind === "item") {
    const i = r.data as { name?: string; rate?: string; tax_rate?: string; hsn_sac?: string | null };
    return (
      <Facts
        items={[
          ["Name", i.name],
          ["Rate", i.rate ? <span className="num">{fmtMoney(i.rate)}</span> : null],
          ["GST", i.tax_rate ? `${i.tax_rate}%` : null],
          ["HSN/SAC", i.hsn_sac ?? null],
        ]}
      />
    );
  }
  if (r.kind === "expense") {
    const e = r.data as Expense;
    return (
      <Facts
        items={[
          ["Date", fmtDate(e.date)],
          ["Amount", <span className="num">{fmtMoney(e.amount)}</span>],
          ["Reference", e.reference],
        ]}
      />
    );
  }
  if (r.kind === "onboarding") {
    const o = r.data as Onboarding;
    return (
      <Facts
        items={[
          ["Client", o.client_name],
          ["Email", o.client_email],
          ["Service", o.service],
          ["Total", <span className="num">{fmtMoney(o.total_amount)}</span>],
          ["Paid", <span className="num">{fmtMoney(o.paid_amount)}</span>],
        ]}
      />
    );
  }
  if (r.kind === "vendor") {
    const v = r.data as VendorDetail;
    return (
      <Facts
        items={[
          ["Email", v.email],
          ["Phone", v.phone],
          ["GSTIN", v.gstin],
        ]}
      />
    );
  }
  if (r.kind === "customer") {
    const c = r.data as CustomerDetail;
    return (
      <>
        <Facts
          items={[
            ["Email", c.email],
            ["Phone", c.phone],
            ["GSTIN", c.gstin],
            ["State", c.state_name],
          ]}
        />
        {c.invoices?.length > 0 && (
          <>
            <h3 className="pwa-h3">{t("Invoices")}</h3>
            <div className="crm-table-wrap is-stack">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>{t("Invoice")}</th>
                    <th>{t("Date")}</th>
                    <th className="is-r">{t("Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.invoices.map((i) => (
                    <tr key={i.installment_id}>
                      <td>
                        <strong>{i.number ?? i.label}</strong>
                        <small>{i.service}</small>
                      </td>
                      <td data-label={t("Date")}>{fmtDate(i.date)}</td>
                      <td data-label={t("Amount")} className="is-r num">
                        {fmtMoney(i.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  }
  const d = r.data as SalesDoc;
  return (
    <>
      <Facts
        items={[
          ["Customer", d.customer_name],
          ["Status", cap(d.status.replace(/_/g, " "))],
          ["Issued", fmtDate(d.issue_date)],
          ["Due", d.due_date ? fmtDate(d.due_date) : null],
          ["Total", <span className="num">{fmtMoney(totalOf(d), d)}</span>],
          ["Paid", <span className="num">{fmtMoney(d.paid, d)}</span>],
          ["Balance due", <strong className="num">{fmtMoney(d.balance, d)}</strong>],
        ]}
      />
      <h3 className="pwa-h3">{t("Lines")}</h3>
      <div className="crm-table-wrap is-stack">
        <table className="crm-table">
          <thead>
            <tr>
              <th>{t("Item")}</th>
              <th className="is-r">{t("Qty")}</th>
              <th className="is-r">{t("Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l, k) => (
              <tr key={k}>
                <td>
                  <strong>{l.name}</strong>
                  {l.description && <small>{l.description}</small>}
                </td>
                <td data-label={t("Qty")} className="is-r num">
                  {l.quantity}
                </td>
                <td data-label={t("Amount")} className="is-r num">
                  {l.amount === "" ? "—" : fmtMoney(l.amount, d)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * After a reload with no connection: what was kept, the outbox, and (wave 6) forms that queue new work there: an expense,
 * a draft invoice or estimate, time, a note on a kept customer or vendor, a payment on a kept invoice. The session comes
 * back by itself when the server answers, and the outbox is sent then. Wave 8 (OfflineActions.tsx): the same queued actions
 * as the full CRM opened online: edits of a kept customer, vendor or draft, issuing with this device's reserved numbers,
 * approvals, and payments on onboarding instalments and Billing invoices. Only this organization's copies, as before.
 */
export function OfflineApp({ profile }: { profile: OfflineProfile }) {
  useLocale(); // the person's language (orgCheck.ts) and its texts arrive after the first render
  const { signOut } = useAuth();
  const items = useOutbox();
  // Wave 8: an edit queued here changes the kept copy (offlineCache.ts patchKept), so the list is read again after each item.
  const kept = useKept(items.length);
  const [open, setOpen] = useState<Kept | null>(null);
  const [outbox, setOutbox] = useState(false);
  const [form, setForm] = useState<OfflineForm | null>(null);
  const [action, setAction] = useState<ActionForm | null>(null);
  // Wave 9: the full forms of the CRM, offline (OfflineFullForms.tsx).
  const [full, setFull] = useState<FullForm | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  useEffect(() => void keptLatest<{ items?: TimeEntry[] }>("/admin/projects/time-entries").then((l) => setEntries((l?.items ?? []).filter((e) => e.staff_id === profile.id && !e.billed).slice(0, 10)), () => setEntries([])), [profile.id, items.length]);
  const [done, setDone] = useState<string | null>(null);
  const doc = open?.kind === "invoice" ? (open.data as SalesDoc) : null;
  const payable = doc && (doc.status === "issued" || doc.status === "partially_paid") && Number(doc.balance) > 0;
  const draft = doc?.status === "draft" ? doc : null;
  const may = (module: string, what: string) => mayDo(profile, module, what);
  const [said, setSaid] = useState(false);
  const queued = (label: string) => {
    setSaid(false);
    setForm(null);
    setAction(null);
    setFull(null);
    setOpen(null);
    setDone(label);
  };
  /** Wave 9: a kept record in its full form. */
  const editFull = (r: Kept): FullForm | null => {
    if (r.kind === "customer") return { kind: "customer", record: r.data as FullRecord<"customer"> };
    if (r.kind === "vendor") return { kind: "vendor", record: r.data as FullRecord<"vendor"> };
    if (r.kind === "item") return { kind: "item", record: r.data as FullRecord<"item"> };
    if (r.kind === "expense") return { kind: "expense", record: r.data as Expense };
    if (r.kind === "estimate") return { kind: "estimate", record: r.data as SalesDoc };
    if (r.kind === "invoice" && (r.data as SalesDoc).status === "draft") return { kind: "invoice", record: r.data as SalesDoc };
    return null;
  };
  const module = (r: Kept) => (r.kind === "customer" ? "customers" : r.kind === "vendor" || r.kind === "expense" ? "purchases" : r.kind === "item" ? "items" : "billing");
  // A record with an edit waiting shows "changes waiting" instead of offering another edit on a copy that is behind.
  const editing = (path: string) => items.some((i) => i.record === path && i.state !== "flagged");
  return (
    <div className="pwa-offline">
      <header className="pwa-offline__head">
        <BrandMark />
        <span>
          <strong>{profile.full_name}</strong>
          <small>{profile.email}</small>
        </span>
      </header>
      <div className="pwa-strip is-offline" role="status">
        <span>
          <strong>{t("You're offline.")}</strong> {t("This is what you opened recently, as it was then. You can change it, issue invoices with this device's reserved numbers, approve and record payments: all of it is sent when the server answers, and the CRM then opens fully.")}
        </span>
      </div>
      {items.length > 0 && (
        <div className="pwa-strip" role="status">
          <span>{t("{n} waiting to be sent.", { n: items.length })}</span>
          <button type="button" className="button button--secondary button--sm" onClick={() => setOutbox(true)}>
            {t("Outbox")}
          </button>
        </div>
      )}
      {done && (
        <div className="pwa-strip" role="status">
          {/* Wave 9: a full form says in its own words what became of it. */}
          <span>{said ? done : t("Kept for sending: {what}", { what: done })}</span>
        </div>
      )}
      <h2 className="pwa-offline__title">{t("Record while offline")}</h2>
      {/* Wave 9: the CRM's full forms, with every field, the lists kept on this device and the server's checks. */}
      <p className="pwa-actions pwa-offline__actions">
        <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "expense" })}>
          {t("New expense")}
        </button>
        <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "invoice" })}>
          {t("New invoice")}
        </button>
        <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "estimate" })}>
          {t("New estimate")}
        </button>
        <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "time" })}>
          {t("Log time")}
        </button>
        {may("customers", "create") && (
          <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "customer" })}>
            {t("New customer")}
          </button>
        )}
        {may("purchases", "create") && (
          <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "vendor" })}>
            {t("New vendor")}
          </button>
        )}
        {may("items", "create") && (
          <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "item" })}>
            {t("New item")}
          </button>
        )}
      </p>
      {entries.length > 0 && (
        <>
          <h2 className="pwa-offline__title">{t("Your time entries")}</h2>
          <ul className="pwa-list">
            {entries.map((e) => (
              <li key={e.id}>
                <button type="button" className="pwa-row" onClick={() => setFull({ kind: "time", record: e })}>
                  <strong>
                    {e.project_name} · {e.hours} h
                  </strong>
                  <small>
                    {fmtDate(e.date)} · {t(cap(e.status))}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {may("billing", "edit") && <BlockNote items={items} />}
      <ApprovalsSection profile={profile} items={items} open={setAction} />
      <BillingSection profile={profile} items={items} open={setAction} />
      <h1 className="pwa-offline__title">{t("Saved for offline")}</h1>
      <KeptList kept={kept} onOpen={setOpen} />
      <p className="pwa-offline__foot">
        <button type="button" className="wo-link" onClick={() => void signOut()}>
          {t("Sign out and delete the offline copies")}
        </button>
      </p>
      {open && (
        <Drawer
          open
          title={open.label}
          sub={t("Saved {when} · changes are kept on this device and sent later", { when: fmtDateTime(new Date(open.saved_at).toISOString()) })}
          onClose={() => setOpen(null)}
          footer={
            open.kind === "customer" || open.kind === "vendor" ? (
              <>
                {may(open.kind === "customer" ? "customers" : "purchases", "edit") &&
                  (editing(open.key.replace(/^o\d+:/, "")) ? (
                    <Pill tone="amber">{t("Changes waiting to be sent")}</Pill>
                  ) : (
                    <button type="button" className="button button--secondary" onClick={() => setFull(editFull(open))}>
                      {t("Edit")}
                    </button>
                  ))}
                <button type="button" className="button button--primary" onClick={() => setForm({ kind: "note", entity: open.kind === "customer" ? "customers" : "vendors", id: idOf(open), name: open.label })}>
                  {t("Add note")}
                </button>
              </>
            ) : draft ? (
              may("billing", "edit") ? (
                <>
                  <button type="button" className="button button--secondary" onClick={() => setFull({ kind: "invoice", record: draft })}>
                    {t("Edit draft")}
                  </button>
                  <button type="button" className="button button--primary" onClick={() => setAction({ kind: "issue", doc: draft })}>
                    {t("Issue offline")}
                  </button>
                </>
              ) : undefined
            ) : payable && doc ? (
              <button type="button" className="button button--primary" onClick={() => setFull({ kind: "payment", doc })}>
                {t("Record payment")}
              </button>
            ) : (open.kind === "estimate" || open.kind === "item" || open.kind === "expense") && may(module(open), "edit") ? (
              editing(open.key.replace(/^o\d+:/, "")) ? (
                <Pill tone="amber">{t("Changes waiting to be sent")}</Pill>
              ) : (
                <button type="button" className="button button--primary" onClick={() => setFull(editFull(open))}>
                  {t("Edit")}
                </button>
              )
            ) : undefined
          }
        >
          <KeptView r={open} />
          {open.kind === "onboarding" && <OnboardingView o={open.data as Onboarding} items={items} profile={profile} open={setAction} />}
        </Drawer>
      )}
      {action && <ActionDrawer form={action} onClose={() => setAction(null)} onQueued={queued} />}
      {full && <FullFormDrawer form={full} onClose={() => setFull(null)} onDone={(m) => (queued(m), setSaid(true))} />}
      {form && (
        <OfflineFormDrawer
          form={form}
          me={profile.id}
          onClose={() => setForm(null)}
          onQueued={queued}
        />
      )}
      {outbox && <OutboxDrawer items={items} onClose={() => setOutbox(false)} />}
    </div>
  );
}

/** Settings › App: install, and how to on an iPhone. */
export function InstallCard() {
  const { canInstall, installed, active } = usePwa();
  const { online } = useNetwork();
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const [note, setNote] = useState<string | null>(null);
  return (
    <section className="crm-block pwa-install">
      <h3>{t("Install the CRM as an app")}</h3>
      <p>{t("On a phone it opens from the home screen without the browser's bars, has shortcuts for a new invoice, a payment and an expense, and keeps working for a while without a connection.")}</p>
      <Facts
        items={[
          ["This device", installed ? t("Installed: running as the app") : t("Not installed, in the browser")],
          ["Offline support", active ? t("On (the app is kept on this device)") : t("Starts after the next reload")],
          ["Connection", online ? t("Online") : t("Offline")],
        ]}
      />
      {!installed && canInstall && (
        <p className="pwa-actions">
          <button type="button" className="button button--primary" onClick={() => void install().then((ok) => setNote(ok ? t("Installed. Open it from your home screen or app list.") : t("Not installed. You can install it later from here.")))}>
            {t("Install app")}
          </button>
        </p>
      )}
      {!installed && !canInstall && (
        <p className="crm-note">
          {ios
            ? t("On an iPhone or iPad: open this page in Safari, tap Share, then Add to Home Screen.")
            : t("Your browser has not offered to install yet. In Chrome or Edge use the install icon in the address bar or the menu's Install app; it needs the site over HTTPS.")}
        </p>
      )}
      {note && <p className="crm-note">{note}</p>}
      <p className="crm-note">
        {t("What stays on the device: the app itself, and the customers and invoices you opened in the last {h} hours, read-only. Sign-out deletes them, and anything not yet sent from the outbox. Your session is never stored on the device.", { h: TTL_HOURS })}
      </p>
    </section>
  );
}
